import { useEffect, useRef } from "react";
import { useStatsigClient, useStatsigUser, type Layer, type PrecomputedEvaluationsInterface } from "@statsig/react-bindings";
import { AnalyticsUtils } from "@/shared/utils/AnalyticsUtils";

type AllowedDefaultValue = string | number | boolean | { [key: string]: AllowedDefaultValue } | AllowedDefaultValue[];

type LayerParameter<K extends string = string, V extends AllowedDefaultValue = AllowedDefaultValue> = {
    key: K;
    defaultValue: V;
};

type Widen<T> = T extends string
    ? string
    : T extends number
      ? number
      : T extends boolean
        ? boolean
        : T extends Array<infer U>
          ? Array<Widen<U>>
          : T extends { [K in keyof T]: T[K] }
            ? { [K in keyof T]: Widen<T[K]> }
            : T;

type Mutable<T> = T extends object ? { -readonly [P in keyof T]: Mutable<T[P]> } : T;

type InferLayerValues<T extends readonly LayerParameter[]> = {
    [P in T[number] as P["key"]]: Mutable<Widen<P["defaultValue"]>>;
};

interface UseStatsigOptions<T extends readonly LayerParameter[]> {
    layerId: string;
    layerParameters: T;
    queryParams?: Record<string, string>;
    deferExposureUntil?: boolean;
}

export function useStatsig<T extends readonly LayerParameter<any>[]>({ layerId, layerParameters, queryParams, deferExposureUntil }: UseStatsigOptions<T>): InferLayerValues<T> {
    const statsigClient = useStatsigClient();
    const { getLayer } = statsigClient;
    const user = useStatsigUser();
    const exposureLoggedRef = useRef(false);

    // Suppress auto-exposure on `.get()`; exposure is enqueued from the useEffect once gating conditions pass.
    const testLayer = getLayer(layerId, { disableExposureLog: true });

    const layerValues = layerParameters.reduce((acc, param) => {
        const value = testLayer.get(param.key, param.defaultValue) as Widen<typeof param.defaultValue>;
        return {
            ...acc,
            [param.key]: value
        };
    }, {} as InferLayerValues<T>);

    useEffect(() => {
        const shouldLogExposure =
            !exposureLoggedRef.current &&
            !!testLayer &&
            !!user.user.userID &&
            !!testLayer.__evaluation?.allocated_experiment_name &&
            areKeysMatching(
                testLayer.__evaluation.explicit_parameters,
                layerParameters.map(param => param.key)
            ) &&
            (deferExposureUntil === undefined || deferExposureUntil === true);

        if (shouldLogExposure) {
            exposureLoggedRef.current = true;

            // getLayer is memoized by options, so calling it without disableExposureLog returns a distinct Layer that fires exposure on `.get()`.
            const exposingLayer = getLayer(layerId);
            layerParameters.forEach(param => {
                exposingLayer.get(param.key, param.defaultValue);
            });

            sendStatsigExposureAction({ userID: user.user.userID, layer: testLayer, queryParams });
        }
    }, [getLayer, layerId, testLayer, user.user.userID, layerParameters, queryParams, deferExposureUntil]);

    return layerValues;
}

/**
 * Logs the layer exposure for `layerParameters` right now, off the client's current evaluation, under the
 * same conditions the hook above applies (a user, an allocated experiment, and every parameter explicit
 * to it). Returns whether an exposure was logged.
 *
 * For a treatment decided inside an event handler rather than a render — e.g. a default installed on the
 * first quote write (ENG-20299) — so the exposure is logged from the SAME evaluation that decided the
 * treatment: a Statsig user update that lands later cannot re-run this and count a quoter under an arm
 * they never got.
 */
export function logLayerExposure({
    client,
    layerId,
    layerParameters,
    queryParams
}: {
    /** The `client` of `useStatsigClient()`. */
    client: PrecomputedEvaluationsInterface;
    layerId: string;
    layerParameters: readonly LayerParameter[];
    queryParams?: Record<string, string>;
}): boolean {
    const userID = client.getContext().user?.userID;
    const layer = client.getLayer(layerId, { disableExposureLog: true });
    const shouldLogExposure =
        !!userID &&
        !!layer.__evaluation?.allocated_experiment_name &&
        areKeysMatching(
            layer.__evaluation.explicit_parameters,
            layerParameters.map(param => param.key)
        );
    if (!shouldLogExposure) return false;

    const exposingLayer = client.getLayer(layerId);
    layerParameters.forEach(param => {
        exposingLayer.get(param.key, param.defaultValue);
    });
    sendStatsigExposureAction({ userID, layer, queryParams });
    return true;
}

function sendStatsigExposureAction({ userID, layer, queryParams }: { userID: string | undefined; layer: Layer; queryParams?: Record<string, string> }) {
    AnalyticsUtils.sendDataDogAction("statsig_exposure", {
        extra: {
            quote: {
                extra: {
                    queryParams,
                    statsig: {
                        userID,
                        [layer.name]: {
                            ruleID: layer.__evaluation?.rule_id,
                            allocatedExperimentName: layer.__evaluation?.allocated_experiment_name
                        }
                    }
                }
            }
        }
    });
}

function areKeysMatching(explicitParameters: string[] | undefined, keysToMatch: string[]): boolean {
    if (!explicitParameters) return false;
    return keysToMatch.every(key => explicitParameters.includes(key));
}
